/**
 * GID-220: parallel, resilient file attachment uploads.
 *
 * The old flow posted one file and held the request open while the server
 * extracted, OCR'd and summarised it -- minutes for a big PDF, during which
 * the attach button stayed disabled and a dropped connection lost the file.
 * The server now answers the POST at once (202) and does the reading in the
 * background, so this module:
 *
 *   - runs at most MAX_CONCURRENT_UPLOADS POSTs at a time (the slot frees as
 *     soon as the server has the bytes, not when it finishes reading them);
 *   - polls each accepted file's status until it is ready or failed;
 *   - retries network errors, 5xx and 429 with backoff. Re-posting is safe
 *     because the server dedupes identical bytes in a session;
 *   - never retries any other 4xx: those are answers (too big, wrong type,
 *     too many files), not accidents.
 *
 * It holds no UI state. The task pane keeps `attachedFiles` as the source of
 * truth and applies the patches reported through onUpdate. The api and the
 * timers are injected so the whole lifecycle runs under Jest fake timers.
 */

/* global setTimeout, clearTimeout, AbortController, console */

export const MAX_FILES_PER_SESSION = 10;
export const MAX_CONCURRENT_UPLOADS = 3;
export const POLL_INTERVAL_MS = 1500;
export const POLL_SLOW_INTERVAL_MS = 3000;
export const POLL_SLOWDOWN_AFTER_MS = 30 * 1000;
export const POLL_TIMEOUT_MS = 15 * 60 * 1000;
export const RETRY_DELAYS_MS = [1000, 2000, 4000];
// Up to this fraction of a retry delay is added at random, so files that
// failed together (one network blip) do not all come back in the same tick.
export const RETRY_JITTER_RATIO = 0.25;
// A 429 can name its own wait; honour it, but never park a file longer than this.
export const MAX_RETRY_AFTER_MS = 30 * 1000;

export const TIMEOUT_ERROR = "Timed out while reading the file";
export const GENERIC_UPLOAD_ERROR = "The file could not be uploaded";
export const GENERIC_READ_ERROR = "The file could not be read";

export const STATUS = Object.freeze({
  UPLOADING: "uploading",
  PROCESSING: "processing",
  READY: "ready",
  FAILED: "failed",
});

const STAGE_LABELS = Object.freeze({
  uploading: "uploading…",
  retrying: "retrying…",
  queued: "waiting…",
  extracting: "reading…",
  summarising: "summarising…",
  indexing: "indexing…",
  done: "finishing…",
});

let localIdCounter = 0;

export function nextLocalId() {
  localIdCounter += 1;
  return `upload-${Date.now().toString(36)}-${localIdCounter}`;
}

export function createPendingEntry(file, sessionId = null, localId = nextLocalId()) {
  return Object.freeze({
    local_id: localId,
    file,
    file_id: null,
    message_id: null,
    filename: file?.name || "file",
    size: Number(file?.size) || 0,
    status: STATUS.UPLOADING,
    stage: "uploading",
    error: null,
    session_id: sessionId,
  });
}

export function isInFlight(entry) {
  return entry?.status === STATUS.UPLOADING || entry?.status === STATUS.PROCESSING;
}

// Failed chips are not counted: the server does not count them either, and a
// user stuck at the limit by a file that never arrived could not add another.
export function countsTowardLimit(entry) {
  return isInFlight(entry) || entry?.status === STATUS.READY;
}

export function remainingSlots(entries, limit = MAX_FILES_PER_SESSION) {
  const used = (entries || []).filter(countsTowardLimit).length;
  return Math.max(0, limit - used);
}

/**
 * Decide whether a pick fits. A pick that does not fit is refused whole rather
 * than trimmed: which of the picked files would be "first" depends on the OS
 * dialog, so a partial accept would look arbitrary.
 * @returns {{accepted: boolean, remaining: number, message: string|null}}
 */
export function checkPickAgainstLimit(entries, pickedCount, limit = MAX_FILES_PER_SESSION) {
  const remaining = remainingSlots(entries, limit);
  if (pickedCount <= remaining) return { accepted: true, remaining, message: null };
  if (remaining === 0) {
    return {
      accepted: false,
      remaining,
      message:
        `This chat already has ${limit} attached files, the most it can hold. ` +
        "Remove one to attach another.",
    };
  }
  const noun = remaining === 1 ? "file" : "files";
  return {
    accepted: false,
    remaining,
    message:
      `A chat can hold ${limit} attached files. You picked ${pickedCount}, ` +
      `but only ${remaining} more ${noun} can be added. Pick ${remaining} or fewer.`,
  };
}

export function stageLabel(entry) {
  if (!entry) return "";
  if (entry.status === STATUS.FAILED) return entry.error || GENERIC_READ_ERROR;
  if (entry.status === STATUS.READY) return "";
  return STAGE_LABELS[entry.stage] || STAGE_LABELS[entry.status] || "working…";
}

export function stillReadingNotice(entries) {
  const names = (entries || []).filter(isInFlight).map((e) => e.filename || "file");
  if (!names.length) return null;
  return `Still reading ${names.join(", ")}. This answer may not include them.`;
}

export function isRetryableError(error) {
  if (!error || error.aborted) return false;
  const status = Number(error.status);
  // No status means the request never got an answer: offline, DNS, reset, timeout.
  if (!status) return true;
  return status === 429 || status >= 500;
}

export function retryDelayMs(attempt, error, random = Math.random) {
  const base = RETRY_DELAYS_MS[Math.min(attempt, RETRY_DELAYS_MS.length - 1)];
  const jittered = base + Math.floor(random() * base * RETRY_JITTER_RATIO);
  const retryAfterMs = Number(error?.retryAfterSeconds) * 1000;
  if (Number.isFinite(retryAfterMs) && retryAfterMs > jittered) {
    return Math.min(retryAfterMs, MAX_RETRY_AFTER_MS);
  }
  return jittered;
}

export function pollIntervalMs(elapsedMs) {
  return elapsedMs < POLL_SLOWDOWN_AFTER_MS ? POLL_INTERVAL_MS : POLL_SLOW_INTERVAL_MS;
}

function readyPatch(result, job) {
  return {
    status: STATUS.READY,
    stage: "done",
    error: null,
    file_id: result.file_id || job.fileId || null,
    message_id: result.message_id || null,
    filename: result.filename || job.file?.name || "file",
    size: Number(result.size ?? job.file?.size) || 0,
    extracted_tokens: result.extracted_tokens,
    was_summarised: Boolean(result.was_summarised),
    was_ocred: Boolean(result.was_ocred),
  };
}

/**
 * @param {Object} deps
 * @param {{attachFileAsync: Function, getAttachmentStatus: Function, deleteAttachedFile: Function}} deps.api
 * @param {(localId: string, patch: Object) => void} deps.onUpdate
 * @param {(localId: string, result: Object) => void} deps.onReady - called once per file
 * @param {Function} [deps.setTimer] / [deps.clearTimer] / [deps.now] / [deps.random]
 * @param {Function} [deps.createAbortController]
 * @param {number} [deps.maxConcurrent]
 */
export function createAttachmentUploader({
  api,
  onUpdate = () => {},
  onReady = () => {},
  setTimer = (fn, ms) => setTimeout(fn, ms),
  clearTimer = (handle) => clearTimeout(handle),
  now = () => Date.now(),
  random = Math.random,
  createAbortController = () => new AbortController(),
  maxConcurrent = MAX_CONCURRENT_UPLOADS,
  logger = console,
} = {}) {
  const jobs = new Map();
  const waiting = [];
  let activeUploads = 0;

  function emit(job, patch) {
    if (job.cancelled) return;
    const changed = Object.keys(patch).some((key) => job.lastReported[key] !== patch[key]);
    if (!changed) return;
    job.lastReported = { ...job.lastReported, ...patch };
    onUpdate(job.localId, patch);
  }

  function fail(job, message) {
    emit(job, { status: STATUS.FAILED, error: message || GENERIC_READ_ERROR });
    finish(job);
  }

  function finish(job) {
    releaseSlot(job);
    if (jobs.get(job.localId) === job) jobs.delete(job.localId);
  }

  function releaseSlot(job) {
    if (!job.holdsSlot) return;
    job.holdsSlot = false;
    activeUploads -= 1;
    pump();
  }

  // A cancellable wait: cancel() resolves it early so the awaiting loop can
  // see job.cancelled and unwind, instead of hanging on a cleared timer.
  function wait(job, ms) {
    return new Promise((resolve) => {
      job.wake = resolve;
      job.timer = setTimer(() => {
        job.timer = null;
        job.wake = null;
        resolve();
      }, ms);
    });
  }

  function pump() {
    while (activeUploads < maxConcurrent && waiting.length) {
      const job = waiting.shift();
      if (job.cancelled) continue;
      job.holdsSlot = true;
      activeUploads += 1;
      runUpload(job).catch((err) => {
        logger.error("Attachment upload crashed:", err);
        fail(job, GENERIC_UPLOAD_ERROR);
      });
    }
  }

  async function runUpload(job) {
    let attempt = 0;
    for (;;) {
      job.controller = createAbortController();
      let result;
      try {
        result = await api.attachFileAsync(job.sessionId, job.file, job.controller.signal);
      } catch (error) {
        if (job.cancelled) return;
        if (attempt < RETRY_DELAYS_MS.length && isRetryableError(error)) {
          emit(job, { status: STATUS.UPLOADING, stage: "retrying" });
          await wait(job, retryDelayMs(attempt, error, random));
          attempt += 1;
          if (job.cancelled) return;
          emit(job, { status: STATUS.UPLOADING, stage: "uploading" });
          continue;
        }
        fail(job, error?.message || GENERIC_UPLOAD_ERROR);
        return;
      }
      if (job.cancelled) return;
      releaseSlot(job);
      handleAccepted(job, result || {});
      return;
    }
  }

  function handleAccepted(job, result) {
    if (result.file_id) job.fileId = result.file_id;
    // An older server ignores ?async=1 and answers the synchronous way: a
    // finished record with a message_id and no status. Treat that as ready.
    const status = result.status || (result.message_id ? STATUS.READY : null);
    if (status === STATUS.READY) return markReady(job, result);
    if (status === STATUS.FAILED) return fail(job, result.error);
    if (!job.fileId) return fail(job, GENERIC_UPLOAD_ERROR);
    emit(job, {
      status: STATUS.PROCESSING,
      stage: result.stage || "queued",
      file_id: job.fileId,
      error: null,
    });
    job.pollStartedAt = now();
    runPolling(job).catch((err) => {
      logger.error("Attachment status polling crashed:", err);
      fail(job, GENERIC_READ_ERROR);
    });
  }

  function markReady(job, result) {
    if (job.readyFired) return;
    job.readyFired = true;
    const patch = readyPatch(result, job);
    emit(job, patch);
    if (!job.cancelled) onReady(job.localId, { ...result, ...patch });
    finish(job);
  }

  async function runPolling(job) {
    let attempt = 0;
    let justBackedOff = false;
    for (;;) {
      const elapsed = now() - job.pollStartedAt;
      if (elapsed >= POLL_TIMEOUT_MS) return fail(job, TIMEOUT_ERROR);
      // A retry backoff stands in for the poll interval rather than adding to it.
      if (!justBackedOff) {
        await wait(job, Math.min(pollIntervalMs(elapsed), POLL_TIMEOUT_MS - elapsed));
        if (job.cancelled) return;
      }
      justBackedOff = false;
      if (now() - job.pollStartedAt >= POLL_TIMEOUT_MS) return fail(job, TIMEOUT_ERROR);

      job.controller = createAbortController();
      let status;
      try {
        status = await api.getAttachmentStatus(job.sessionId, job.fileId, job.controller.signal);
      } catch (error) {
        if (job.cancelled) return;
        if (attempt < RETRY_DELAYS_MS.length && isRetryableError(error)) {
          await wait(job, retryDelayMs(attempt, error, random));
          attempt += 1;
          if (job.cancelled) return;
          justBackedOff = true;
          continue;
        }
        return fail(job, error?.message || GENERIC_READ_ERROR);
      }
      if (job.cancelled) return;
      attempt = 0;
      const state = status || {};
      if (state.status === STATUS.READY) return markReady(job, state);
      if (state.status === STATUS.FAILED) return fail(job, state.error);
      emit(job, { status: STATUS.PROCESSING, stage: state.stage || "queued" });
    }
  }

  function stopJob(job) {
    job.cancelled = true;
    job.controller?.abort();
    if (job.timer != null) clearTimer(job.timer);
    job.timer = null;
    const wake = job.wake;
    job.wake = null;
    wake?.();
    const queuedAt = waiting.indexOf(job);
    if (queuedAt >= 0) waiting.splice(queuedAt, 1);
    finish(job);
  }

  /** Start (or restart, for the retry button) uploading a file. */
  function enqueue({ localId, file, sessionId }) {
    const previous = jobs.get(localId);
    if (previous) stopJob(previous);
    const job = {
      localId,
      file,
      sessionId,
      fileId: null,
      cancelled: false,
      readyFired: false,
      holdsSlot: false,
      controller: null,
      timer: null,
      wake: null,
      pollStartedAt: 0,
      lastReported: {},
    };
    jobs.set(localId, job);
    emit(job, { status: STATUS.UPLOADING, stage: "uploading", error: null });
    waiting.push(job);
    pump();
  }

  /** Stop all client-side work for a file. Server-side work is left alone. */
  function cancel(localId) {
    const job = jobs.get(localId);
    if (job) stopJob(job);
  }

  function cancelAll() {
    // Empty the queue first: each stop frees a slot, and pump() would
    // otherwise start the next queued file on the way out.
    waiting.length = 0;
    for (const job of [...jobs.values()]) stopJob(job);
  }

  /**
   * Remove a file the user dismissed. Ready and processing files are deleted
   * on the server first (a processing one by file_id, which cancels it) and
   * this rejects if that fails, leaving the file and its polling untouched.
   * A file still being posted is aborted; nothing exists server-side to delete.
   * A failed file is dropped at once, with a best-effort server delete in case
   * the server is in fact still reading it (the 15-minute give-up).
   */
  async function remove(entry) {
    const job = jobs.get(entry.local_id);
    const sessionId = entry.session_id || job?.sessionId;
    const fileId = entry.file_id || job?.fileId || null;
    const serverId = entry.message_id || fileId;

    if (entry.status === STATUS.FAILED) {
      if (job) stopJob(job);
      if (serverId && sessionId) {
        api.deleteAttachedFile(sessionId, serverId).catch((err) => {
          logger.warn("Could not clear a failed attachment on the server:", err?.message || err);
        });
      }
      return;
    }
    if (!serverId) {
      if (job) stopJob(job);
      return;
    }
    if (!sessionId) throw new Error("No chat session for this file");
    await api.deleteAttachedFile(sessionId, serverId);
    const current = jobs.get(entry.local_id);
    if (current) stopJob(current);
  }

  function isActive(localId) {
    return jobs.has(localId);
  }

  function activeUploadCount() {
    return activeUploads;
  }

  return { enqueue, cancel, cancelAll, remove, isActive, activeUploadCount };
}
